import { MarketAccessory, MarketButton } from '@market/react';
import { useTranslation } from 'react-i18next';
import { MarketMinusIcon, MarketPlusIcon } from '@squareup/market-react/icons';
import { MarketSlider } from '@squareup/market-react/trial';
import styles from './PercentagePicker.module.css';

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 100;
const DEFAULT_STEP = 5;

export interface PercentagePickerProps {
  value: number;
  size?: 'normal' | 'small';
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: number) => void;
}

export const PercentagePicker = ({
  value,
  onChange,
  size = 'normal',
  min = DEFAULT_MIN,
  max = DEFAULT_MAX,
  step = DEFAULT_STEP,
}: PercentagePickerProps) => {
  const { t } = useTranslation();

  const handleDecrement = () => {
    onChange(Math.max(value - step, min));
  };

  const handleIncrement = () => {
    onChange(Math.min(value + step, max));
  };

  return (
    <>
      <div className={styles.percentageControl}>
        <span className={size === 'small' ? styles.percentageValueSmall : styles.percentageValue}>
          {value}%
        </span>
        <div className={styles.buttons}>
          <MarketButton
            icon-only
            rank="secondary"
            size={size === 'small' ? 'small' : 'medium'}
            disabled={value <= min}
            aria-label={t('bankingShared.creditCard.percentagePicker.decrement')}
            onClick={handleDecrement}
          >
            <MarketAccessory slot="icon">
              <MarketMinusIcon />
            </MarketAccessory>
          </MarketButton>
          <MarketButton
            icon-only
            rank="secondary"
            size={size === 'small' ? 'small' : 'medium'}
            disabled={value >= max}
            aria-label={t('bankingShared.creditCard.percentagePicker.increment')}
            onClick={handleIncrement}
          >
            <MarketAccessory slot="icon">
              <MarketPlusIcon />
            </MarketAccessory>
          </MarketButton>
        </div>
      </div>

      <div className={styles.sliderContainer}>
        <span className={`semibold-20 ${styles.sliderLabel}`}>{min}%</span>
        <div className={styles.slider}>
          <MarketSlider
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={onChange}
          />
        </div>
        <span className={`semibold-20 ${styles.sliderLabel}`}>{max}%</span>
      </div>
    </>
  );
};
